import { useSearchParams } from 'react-router-dom';

import { AnalyticsProfile, InsightsEngagementType } from 'api';
import { CompaniesFilter, CompaniesQueryParams, PeopleFilter, PeopleQueryParams } from './types';
import { MAX_ADVANCED_FILTER_QUERIES } from 'app-constants';

export const getEngagementType = (type: InsightsEngagementType) => {
  switch (type) {
    case InsightsEngagementType.Comment:
      return 'Comment';
    case InsightsEngagementType.Like:
      return 'Like';
    case InsightsEngagementType.CommentReaction:
      return 'Comment Reaction';
    case InsightsEngagementType.Quote:
      return 'Quote';
    case InsightsEngagementType.Retweet:
      return 'Retweet';
    case InsightsEngagementType.Reaction:
      return 'Reaction';
    default:
      return '';
  }
};

export const getTitles = (profile: AnalyticsProfile) => {
  const { title } = profile;

  if (!title) {
    return '';
  }

  return title?.split(',').join(' - ') || '';
};

export const getRoles = (profile: AnalyticsProfile) => {
  const { role } = profile;
  if (!role) {
    return '';
  }

  return role
    ?.split(' - ')
    .map(role => {
      return role.split('_').join(' ');
    })
    .join(' - ');
};

export const OrganicInsightsCompanyParams = (
  defaultQueryParams: CompaniesQueryParams
): [CompaniesFilter, (filter: any) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();

  for (const [key, value] of searchParams.entries()) {
    if (key == 'hide_company') {
      defaultQueryParams[key] = value === 'true';
      continue;
    }
    if (key == 'users' && value == '') {
      delete defaultQueryParams['users'];
      continue;
    }
    if (key == 'posts' && value == '') {
      delete defaultQueryParams['posts'];
      continue;
    }
    if (key == 'company_pages' && value == '') {
      delete defaultQueryParams['company_pages'];
      continue;
    }
    defaultQueryParams[key] = value;
  }

  return [
    {
      start: defaultQueryParams.start,
      end: defaultQueryParams.end,
      hide_company: defaultQueryParams.hide_company,
      ...(defaultQueryParams['users']?.split(',')?.length
        ? { users: defaultQueryParams['users'].split(',') }
        : null),
      ...(defaultQueryParams['posts']?.split(',')?.length
        ? { posts: defaultQueryParams['posts'].split(',') }
        : null),
      ...(defaultQueryParams['company_pages']?.split(',')?.length
        ? { company_pages: defaultQueryParams['company_pages'].split(',') }
        : null),
      sort_by: JSON.parse(defaultQueryParams['sort_by']),
    },
    setSearchParams,
  ];
};

export const OrganicInsightsPeopleParams = (
  defaultQueryParams: PeopleQueryParams
): [PeopleFilter, (filter: any) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();

  for (const [key, value] of searchParams.entries()) {
    if (key == 'hide_teammates') {
      defaultQueryParams[key] = value === 'true';
      continue;
    }
    if (key == 'users' && value == '') {
      delete defaultQueryParams['users'];
      continue;
    }
    if (key == 'posts' && value == '') {
      delete defaultQueryParams['posts'];
      continue;
    }
    if (key == 'company_pages' && value == '') {
      delete defaultQueryParams['company_pages'];
      continue;
    }
    defaultQueryParams[key] = value;
  }

  return [
    {
      ...defaultQueryParams,
      ...(defaultQueryParams['users']?.split(',')?.length
        ? { users: defaultQueryParams['users'].split(',') }
        : null),
      ...(defaultQueryParams['posts']?.split(',')?.length
        ? { posts: defaultQueryParams['posts'].split(',') }
        : null),
      ...(defaultQueryParams['company_pages']?.split(',')?.length
        ? { company_pages: defaultQueryParams['company_pages'].split(',') }
        : null),
      sort_by: JSON.parse(defaultQueryParams['sort_by']),
    },
    setSearchParams,
  ];
};

export const hasReachedMaxQueries = (tempFilter: PeopleFilter | CompaniesFilter) => {
  const advancedFilterFields = ['users'];
  let hasReachedLimit = false;
  for (const field of advancedFilterFields) {
    if (tempFilter[field]?.length >= MAX_ADVANCED_FILTER_QUERIES) {
      hasReachedLimit = true;
    }
  }
  return hasReachedLimit;
};
